import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import CustomExport from "../../../components/CustomExport/CustomExport";
import { useFetchBulkScanHistoryMutation } from "../../../features/tenant/bulkScanHistory/bulkScanHistoryApi";
import BulkScanDetailsModal from "./BulkScanDetailsModal";
import { HiOutlineSearch, HiOutlineFilter, HiOutlineEye, HiOutlineDownload } from "react-icons/hi";

const BulkScanHistory = () => {
  const initialFilters = {
    name: "",
    mobile: "",
    status: "",
    startDate: "",
    endDate: "",
  };

  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [csvData, setCsvData] = useState([]);

  const [showDetails, setShowDetails] = useState(false);
  const [selectedScanId, setSelectedScanId] = useState(null);

  const [fetchHistory, { isLoading }] = useFetchBulkScanHistoryMutation();

  const handleFetchData = async (currentPage = page, currentLimit = limit, currentFilters = filters) => {
    try {
      const response = await fetchHistory({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        body: {
          ...currentFilters,
          limit: currentLimit,
          offset: (currentPage - 1) * currentLimit,
        },
      }).unwrap();

      if (response.status) {
        setData(response.body.data || []);
        setTotal(response.body.total || 0);
      } else {
        toast.error(response.message || "Failed to fetch history");
      }
    } catch (error) {
      console.error("Fetch history error:", error);
      toast.error(error?.body?.message || "Something went wrong while fetching history");
    }
  };

  const handleExportCSV = async () => {
    toast.loading("Preparing export data...");
    try {
      const response = await fetchHistory({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        body: {
          ...filters,
          limit: 99999, // Fetch all for export
          offset: 0,
        },
      }).unwrap();

      if (response.status) {
        setCsvData(response.data.data || []);
        toast.dismiss();
      } else {
        toast.dismiss();
        toast.error("Failed to prepare export data");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Export error:", error);
      toast.error("Error preparing export data");
    }
  };

  useEffect(() => {
    handleFetchData();
  }, []);

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    handleFetchData(pageNumber, limit, filters);
  };

  const handlePerRowsChange = (newLimit, currentPage) => {
    setLimit(newLimit);
    setPage(1);
    handleFetchData(1, newLimit, filters);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    handleFetchData(1, limit, filters);
  };

  const getStatusBadge = (status_text) => {
    const status = status_text?.toLowerCase();
    let classes = "bg-gray-100 text-gray-700";

    if (status === "success") classes = "bg-green-100 text-green-700 border-green-200";
    else if (status === "pending") classes = "bg-yellow-100 text-yellow-700 border-yellow-200";
    else if (status === "not found" || status === "already scanned" || status === "already locked") classes = "bg-red-100 text-red-700 border-red-200";

    return (
      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${classes}`}>
        {status_text}
      </span>
    );
  };

  const columns = [
    {
      name: "SNo",
      selector: (row, i) => (page - 1) * limit + i + 1,
      width: "70px",
    },
    {
      name: "User Details",
      cell: (row) => (
        <div className="py-2">
          <p className="font-bold text-gray-900">{row.user_name || "N/A"}</p>
          <p className="text-xs text-gray-500">{row.user_mobile}</p>
        </div>
      ),
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Count",
      selector: (row) => row.count,
      sortable: true,
      width: "100px",
      center: true,
    },
    {
      name: "Status",
      cell: (row) => getStatusBadge(row.status_text),
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Created At",
      selector: (row) => new Date(row.created_at).toLocaleString(),
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          onClick={() => {
            setSelectedScanId(row.id);
            setShowDetails(true);
          }}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors group"
          title="View Details"
        >
          <HiOutlineEye className="w-5 h-5 transition-transform group-hover:scale-110" />
        </button>
      ),
      width: "100px",
      center: true,
    },
  ];

  const inputClass = "block w-full px-4 py-2 text-sm text-gray-900 border border-gray-200 rounded-xl bg-gray-50/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all outline-none";

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 px-6 shadow-sm">
        <div className="border-b bg-white border-gray-300  py-2 shadow-sm text-xl font-bold">
          Bulk Scan History
          <LinksComponent />
        </div>
        {/* <div className="flex items-center gap-3">
            <CustomExport
              fetchData={handleExportCSV}
              data={csvData}
              defaultColumnExport={["id", "user_name", "user_mobile", "count", "status_text", "created_at"]}
              filename="bulk_scan_history"
            />
          </div> */}

      </div>

      <div className="p-4 max-w-[1600px] mx-auto space-y-6">
        {/* Filter Section */}
        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-100">
          <form onSubmit={onSearch} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">Name</label>
              <input
                type="text"
                className={inputClass}
                placeholder="Search name..."
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">Mobile</label>
              <input
                type="text"
                className={inputClass}
                placeholder="Search mobile..."
                value={filters.mobile}
                onChange={(e) => setFilters({ ...filters, mobile: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">Status</label>
              <select
                className={inputClass}
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Statuses</option>
                <option value="Success">Success</option>
                <option value="Pending">Pending</option>
                <option value="Not Found">Not Found</option>
                <option value="Already Locked">Already Locked</option>
                <option value="Already Scanned">Already Scanned</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">Start Date</label>
              <input
                type="date"
                className={inputClass}
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">End Date</label>
              <input
                type="date"
                className={inputClass}
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>

            <button
              type="submit"
              className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-100 active:scale-95 h-[42px]"
            >
              <HiOutlineSearch className="w-5 h-5" />
              <span>Search</span>
            </button>
          </form>
        </div>

        {/* Data Table Section */}
        <div className="bg-white rounded-md shadow-sm border border-gray-100 overflow-hidden">
          <DataTableComponent
            columns={columns}
            data={data}
            loading={isLoading}
            totalRows={total}
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
            paginationServer
            highlightOnHover
            customStyles={{
              table: {
                style: {
                  backgroundColor: 'white',
                },
              },
              rows: {
                style: {
                  minHeight: '72px',
                  '&:not(:last-of-type)': {
                    borderBottomColor: '#f3f4f6',
                  },
                },
              },
              headRow: {
                style: {
                  backgroundColor: '#f9fafb',
                  borderBottomColor: '#e5e7eb',
                  minHeight: '52px',
                },
              },
              headCells: {
                style: {
                  fontSize: '10px',
                  fontWeight: '900',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                },
              },
            }}
          />
        </div>
      </div>

      <BulkScanDetailsModal
        show={showDetails}
        setShow={setShowDetails}
        scanId={selectedScanId}
      />
    </div>
  );
};

export default BulkScanHistory;
